/**
 * Compares two values for equality.
 * Handles the quirk where initial array values come wrapped in an extra array (e.g., [['value']] vs ['value']).
 * Empty values (null, undefined, '' and empty lists) are all treated as "no answer", since the API omits
 * empty answers while the form reports blank fields as '' or an empty list.
 * @param {*} current - Current value from form
 * @param {*} initial - Initial value (may be wrapped in extra array for multi-select fields)
 * @returns {boolean} True if values are equal
 */
export function valuesAreEqual(current, initial) {
  // Unwrap nested arrays - initial values for multi-select come as [['value']] instead of ['value']
  const unwrap = (val) => Array.isArray(val) && val.length === 1 && Array.isArray(val[0]) ? val[0] : val;
  // Only these count as empty - 0 and false are real answers
  const isEmpty = (val) => val == null || val === '' || (Array.isArray(val) && val.length === 0);
  const currentVal = unwrap(current);
  const initialVal = unwrap(initial);

  if (isEmpty(currentVal) && isEmpty(initialVal)) return true;
  if (currentVal == null || initialVal == null) return false;

  // Arrays - sort before comparing to handle order differences in multi-select
  if (Array.isArray(currentVal) && Array.isArray(initialVal)) {
    if (currentVal.length !== initialVal.length) return false;
    return JSON.stringify([...currentVal].sort()) === JSON.stringify([...initialVal].sort());
  }

  // Objects - sort keys before comparing to handle different property orders
  if (typeof currentVal === 'object' && typeof initialVal === 'object') {
    const sortedStringify = (obj) => JSON.stringify(
      Object.keys(obj).sort().reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {})
    );
    return sortedStringify(currentVal) === sortedStringify(initialVal);
  }

  // Primitives
  return currentVal === initialVal;
}

/**
 * Filters form values to only include fields that have changed from their initial values.
 * This prevents sending view-only field values to the API which would cause rejection.
 * @param {Object} currentValues - Current form values
 * @param {Object} initialValues - Initial values loaded from API
 * @returns {Object} Only the changed values
 */
export function getChangedValues(currentValues, initialValues) {
  if (!currentValues) return {};
  if (!initialValues) return currentValues;

  const changedValues = {};
  for (const key of Object.keys(currentValues)) {
    if (!valuesAreEqual(currentValues[key], initialValues[key])) {
      changedValues[key] = currentValues[key];
    }
  }
  return changedValues;
}



// WEBPACK FOOTER //
// src/common/form-save-helpers.js